import api from '../services/api';

// ─── Status ───────────────────────────────────────────────────────────────────

// Fluxo: Aprovação 1º nível → Aprovação 2º nível → Pendente → Em atendimento → Concluída
//                           ↘ Devolvida para ajustes (de qualquer nível) ↗ (correção e reenvio recomeça do 1º)
export const MARKETING_STATUSES = ['em_aprovacao', 'em_aprovacao_2', 'devolvida_ajustes', 'pendente', 'em_atendimento', 'concluida', 'recusada'] as const;

export type MarketingStatus = (typeof MARKETING_STATUSES)[number];

export const MARKETING_STATUS_LABELS: Record<MarketingStatus, string> = {
    em_aprovacao: 'Aprovação 1º nível',
    em_aprovacao_2: 'Aprovação 2º nível',
    devolvida_ajustes: 'Devolvida para ajustes',
    pendente: 'Pendente',
    em_atendimento: 'Em atendimento',
    concluida: 'Concluída',
    recusada: 'Recusada',
};

export const MARKETING_STATUS_DESCRICOES: Record<MarketingStatus, string> = {
    em_aprovacao: 'Aguardando a análise do 1º aprovador.',
    em_aprovacao_2: 'Aprovada no 1º nível; aguardando o 2º aprovador liberar para o atendimento.',
    devolvida_ajustes: 'Um aprovador pediu correções ao solicitante.',
    pendente: 'Aprovada e aguardando alguém do atendimento assumir.',
    em_atendimento: 'Alguém do atendimento está trabalhando nela.',
    concluida: 'Atendimento finalizado.',
    recusada: 'Recusada por um aprovador — encerrada sem atendimento.',
};

export const MARKETING_STATUS_BADGE: Record<MarketingStatus, string> = {
    em_aprovacao: 'bg-warning text-white',
    em_aprovacao_2: 'bg-secondary text-white',
    devolvida_ajustes: 'bg-[#f97316] text-white',
    pendente: 'bg-info text-white',
    em_atendimento: 'bg-primary-brand text-on-primary',
    concluida: 'bg-success text-white',
    recusada: 'bg-danger text-white',
};

export const MARKETING_STATUS_OPTIONS = MARKETING_STATUSES.map((s) => ({ value: s, label: MARKETING_STATUS_LABELS[s] }));

/** Status ainda em andamento — usado nos filtros "em aberto". */
export const MARKETING_STATUS_ABERTOS: MarketingStatus[] = ['em_aprovacao', 'em_aprovacao_2', 'devolvida_ajustes', 'pendente', 'em_atendimento'];

// ─── Aprovação em dois níveis ─────────────────────────────────────────────────

export const MARKETING_APROVADOR_PERMISSIONS = ['marketing.aprovador1', 'marketing.aprovador2'] as const;

/** Status da fila de aprovação por permissão — o aprovador 1 decide no 1º nível, o 2 no 2º. */
export const MARKETING_FILA_POR_NIVEL: Record<(typeof MARKETING_APROVADOR_PERMISSIONS)[number], MarketingStatus> = {
    'marketing.aprovador1': 'em_aprovacao',
    'marketing.aprovador2': 'em_aprovacao_2',
};

export function ehAprovador(hasPermission: (p: string) => boolean): boolean {
    return MARKETING_APROVADOR_PERMISSIONS.some(hasPermission);
}

/** Status que o usuário logado consegue decidir (vazio para quem não aprova). */
export function statusFilaAprovacao(hasPermission: (p: string) => boolean): MarketingStatus[] {
    return MARKETING_APROVADOR_PERMISSIONS.filter(hasPermission).map((p) => MARKETING_FILA_POR_NIVEL[p]);
}

// ─── Prioridade (definida pelo aprovador) ─────────────────────────────────────

/** 1 = mais urgente, 5 = menos urgente. Nula enquanto ninguém classificou. */
export const MARKETING_PRIORIDADES = [1, 2, 3, 4, 5] as const;

export type MarketingPrioridade = (typeof MARKETING_PRIORIDADES)[number];

export const MARKETING_PRIORIDADE_LABELS: Record<MarketingPrioridade, string> = {
    1: 'Máxima',
    2: 'Alta',
    3: 'Média',
    4: 'Baixa',
    5: 'Mínima',
};

export const MARKETING_PRIORIDADE_BADGE: Record<MarketingPrioridade, string> = {
    1: 'bg-danger text-white',
    2: 'bg-[#f97316] text-white',
    3: 'bg-warning text-white',
    4: 'bg-info text-white',
    5: 'bg-gray-400 text-white dark:bg-surface-hover dark:text-white-dark',
};

export function ehPrioridade(v: unknown): v is MarketingPrioridade {
    return typeof v === 'number' && (MARKETING_PRIORIDADES as readonly number[]).includes(v);
}

export function prioridadeLabel(v: number | null | undefined): string {
    return ehPrioridade(v) ? `P${v} - ${MARKETING_PRIORIDADE_LABELS[v]}` : 'Sem prioridade';
}

/** Opções de select: "1 - Máxima" … "5 - Mínima". */
export function prioridadeOptions(): { value: MarketingPrioridade; label: string }[] {
    return MARKETING_PRIORIDADES.map((p) => ({ value: p, label: `${p} - ${MARKETING_PRIORIDADE_LABELS[p]}` }));
}

// ─── Campos que o aprovador pode apontar ao devolver ──────────────────────────

export const MARKETING_CAMPOS = ['tipo_material', 'sera_impresso', 'dimensoes', 'formato', 'descricao', 'informacoes', 'marcas', 'publico', 'data_uso', 'motivo_prazo_curto', 'anexos'] as const;

export type MarketingCampo = (typeof MARKETING_CAMPOS)[number];

export const MARKETING_CAMPO_LABELS: Record<MarketingCampo, string> = {
    tipo_material: 'Tipo de material',
    sera_impresso: 'Será impresso?',
    dimensoes: 'Dimensões',
    formato: 'Formato',
    descricao: 'Descrição',
    informacoes: 'Informações complementares',
    marcas: 'Marcas',
    publico: 'Público',
    data_uso: 'Data de uso',
    motivo_prazo_curto: 'Motivo do prazo curto',
    anexos: 'Anexos e referências',
};

export function campoLabel(campo: string): string {
    return MARKETING_CAMPO_LABELS[campo as MarketingCampo] ?? campo;
}

// ─── Filtros iniciais do painel (por permissão) ───────────────────────────────

export type MarketingPresetId = 'abertas' | 'aprovacao' | 'meus_atendimentos' | 'minhas' | 'concluidas' | 'recusadas' | 'todas';

export type MarketingPreset = {
    id: MarketingPresetId;
    label: string;
    descricao: string;
    /** Vazio = todos os status que o usuário enxerga (concluídas continuam fora). */
    status: MarketingStatus[];
    escopo?: 'minhas' | 'meu_atendimento';
    incluirConcluidas?: boolean;
    permission?: string;
    /** Alternativa ao `permission`: basta ter uma delas. */
    anyOf?: readonly string[];
};

// Sem status marcado, a listagem já vem na ordem do fluxo e sem as concluídas —
// não é preciso encher o seletor de chips para chegar ao estado padrão.
const MARKETING_PRESETS: MarketingPreset[] = [
    { id: 'abertas', label: 'Em aberto', descricao: 'Tudo que ainda está em andamento, na ordem do fluxo.', status: [] },
    // O status real da fila depende do nível do usuário — resolvido em `presetPorId`.
    { id: 'aprovacao', label: 'Em aprovação', descricao: 'Solicitações aguardando sua análise.', status: [], anyOf: MARKETING_APROVADOR_PERMISSIONS },
    { id: 'meus_atendimentos', label: 'Meus atendimentos', descricao: 'As que você assumiu.', status: ['em_atendimento'], escopo: 'meu_atendimento', permission: 'marketing.atendimento' },
    { id: 'minhas', label: 'Minhas solicitações', descricao: 'As que você abriu.', status: [], escopo: 'minhas' },
    { id: 'concluidas', label: 'Concluídas', descricao: 'Atendimentos finalizados.', status: ['concluida'] },
    { id: 'recusadas', label: 'Recusadas', descricao: 'Recusadas por um aprovador.', status: ['recusada'] },
    { id: 'todas', label: 'Todas', descricao: 'Todos os status, incluindo concluídas e recusadas.', status: [], incluirConcluidas: true },
];

export function presetsDisponiveis(hasPermission: (p: string) => boolean): MarketingPreset[] {
    return MARKETING_PRESETS.filter((p) => (!p.permission || hasPermission(p.permission)) && (!p.anyOf || p.anyOf.some(hasPermission)));
}

/**
 * Quem aprova cai direto na fila de aprovação; os demais veem tudo que está
 * em aberto (a fila de aprovação alheia nem chega para quem não aprova).
 */
export function presetInicial(hasPermission: (p: string) => boolean): MarketingPresetId {
    return ehAprovador(hasPermission) ? 'aprovacao' : 'abertas';
}

/**
 * O preset "Em aprovação" filtra só o nível que o usuário decide (aprovador 1
 * vê `em_aprovacao`, aprovador 2 vê `em_aprovacao_2`, quem tem os dois vê ambos).
 */
export function presetPorId(id: MarketingPresetId, hasPermission?: (p: string) => boolean): MarketingPreset {
    const preset = MARKETING_PRESETS.find((p) => p.id === id) ?? MARKETING_PRESETS[MARKETING_PRESETS.length - 1];
    if (preset.id === 'aprovacao' && hasPermission) {
        return { ...preset, status: statusFilaAprovacao(hasPermission) };
    }
    return preset;
}

// ─── Tipo de material ─────────────────────────────────────────────────────────

export const TIPO_MATERIAL_VALUES = [
    'arte_redes',
    'apresentacao',
    'comunicacao_visual',
    'card_comercial',
    'comunicado',
    'disparo',
    'logos_marca',
    'motion',
    'video',
    'panfleto',
    'mini_guia',
    'plotagem',
    'banner',
    'documentos',
    'outro',
] as const;

export type TipoMaterialValue = (typeof TIPO_MATERIAL_VALUES)[number];

export const TIPO_MATERIAL_LABELS: Record<TipoMaterialValue, string> = {
    arte_redes: 'Criar arte para redes sociais',
    apresentacao: 'Criar/embelezar apresentação',
    comunicacao_visual: 'Comunicação visual (stand, decorado, obra)',
    card_comercial: 'Card comercial',
    comunicado: 'Comunicado',
    disparo: 'Disparo de e-mail/sms',
    logos_marca: 'Enviar logos ou arquivos de marca',
    motion: 'Produção de motion',
    video: 'Produção de vídeo',
    panfleto: 'Panfleto',
    mini_guia: 'Mini Guia',
    plotagem: 'Plotagem/adesivo/lona',
    banner: 'Banner/Faixa',
    documentos: 'Documentos (minuta de contrato)',
    outro: 'Outro (descrever)',
};

export const TIPO_MATERIAL_DIAS: Record<TipoMaterialValue, number | null> = {
    arte_redes: 3,
    apresentacao: 7,
    comunicacao_visual: 15,
    card_comercial: 3,
    comunicado: 2,
    disparo: 1,
    logos_marca: 1,
    motion: 7,
    video: 7,
    panfleto: 5,
    mini_guia: 5,
    plotagem: 5,
    banner: 2,
    documentos: 7,
    outro: null,
};

// ─── Opções de formulário ─────────────────────────────────────────────────────

export const MARCAS_OPTIONS = [
    { value: 'capital', label: 'Capital' },
    { value: 'morar', label: 'Morar' },
    { value: 'britamazon', label: 'Britamazon' },
    { value: 'mixbeton', label: 'Mixbeton' },
    { value: 'cmv', label: 'CMV' },
    { value: 'seta', label: 'Seta' },
    { value: 'empreendimento', label: 'Empreendimento específico' },
] as const;

export const PUBLICO_OPTIONS = [
    { value: 'clientes', label: 'Clientes' },
    { value: 'corretores', label: 'Corretores' },
    { value: 'parceiros', label: 'Parceiros' },
    { value: 'fornecedores', label: 'Fornecedores' },
    { value: 'interno', label: 'Público interno' },
    { value: 'geral', label: 'Público geral' },
    { value: 'outros', label: 'Outros' },
] as const;

export const SERA_IMPRESSO_OPTIONS = [
    { value: 'sim', label: 'Sim' },
    { value: 'nao', label: 'Não' },
    { value: 'nao_sei', label: 'Não sei informar' },
];

export const DIMENSAO_UNIDADES = [
    { value: 'pixels', label: 'Pixels' },
    { value: 'cm', label: 'Centímetros' },
    { value: 'metros', label: 'Metros (L x A)' },
    { value: 'a5', label: 'A5' },
    { value: 'a4', label: 'A4' },
    { value: 'a3', label: 'A3' },
];

export const FORMATO_OPTIONS = [
    { value: 'horizontal', label: 'Horizontal' },
    { value: 'vertical', label: 'Vertical' },
    { value: 'quadrado', label: 'Quadrado' },
    { value: 'outro', label: 'Outro' },
];

// ─── Domain types ─────────────────────────────────────────────────────────────

export type MarketingUsuarioResumo = { id: number; name: string; email?: string };

/** Ações que o usuário atual pode executar — calculadas no back-end. */
export type MarketingAcoes = {
    aprovar: boolean;
    devolver: boolean;
    recusar: boolean;
    reenviar: boolean;
    assumir_atendimento: boolean;
    concluir: boolean;
    /** Aprovador (qualquer nível) ou admin do módulo, enquanto não encerrada. */
    definir_prioridade: boolean;
    editar: boolean;
    excluir: boolean;
    gerenciar_anexos: boolean;
};

export type MarketingDevolucao = {
    motivo: string | null;
    campos: string[];
    campos_labels: string[];
    devolvida_em: string | null;
    devolvida_por: MarketingUsuarioResumo | null;
    pendente: boolean;
};

export type MarketingRecusa = {
    motivo: string | null;
    recusada_em: string;
    recusada_por: MarketingUsuarioResumo | null;
};

export type MarketingSolicitacao = {
    id: number;
    solicitante_id: number;
    responsavel_cadastro_id: number | null;
    setor_id: number | null;
    tipo_material: TipoMaterialValue;
    tipo_material_label?: string;
    tipo_material_outro: string | null;
    sera_impresso: string | null;
    dimensao_unidade: string | null;
    dimensao_largura: string | null;
    dimensao_altura: string | null;
    formato: string | null;
    formato_outro: string | null;
    descricao: string;
    marcas: string[] | null;
    marca_empreendimento: string | null;
    informacoes: string | null;
    publico: string[] | null;
    publico_outro: string | null;
    data_uso: string;
    motivo_prazo_curto: string | null;
    status: MarketingStatus;
    status_label?: string;
    /** 1 ou 2 enquanto está em aprovação; null fora dela. */
    nivel_aprovacao: 1 | 2 | null;
    /** 1 (mais urgente) a 5; null enquanto o aprovador não classificou. */
    prioridade: MarketingPrioridade | null;
    prioridade_label: string | null;
    prioridade_definida_em: string | null;
    prioridade_definida_por: MarketingUsuarioResumo | null;
    prazo_dias_uteis: number | null;
    data_solicitacao: string;
    data_prazo: string | null;
    data_encerramento: string | null;
    atendente_id: number | null;
    atendente: MarketingUsuarioResumo | null;
    atendimento_iniciado_em: string | null;
    aprovada1_em: string | null;
    aprovada1_por: MarketingUsuarioResumo | null;
    aprovada_em: string | null;
    aprovada_por: MarketingUsuarioResumo | null;
    reenviada_em: string | null;
    devolucao: MarketingDevolucao | null;
    recusa: MarketingRecusa | null;
    created_at: string;
    updated_at: string;
    solicitante?: MarketingUsuarioResumo | null;
    setor?: { id: number; name: string } | null;
    responsavel_cadastro?: MarketingUsuarioResumo | null;
    links?: MarketingLink[];
    acoes: MarketingAcoes;
};

export type MarketingMovimentacao = {
    id: number;
    acao: 'criacao' | 'aprovacao_1' | 'aprovacao' | 'devolucao' | 'recusa' | 'reenvio' | 'atendimento_iniciado' | 'conclusao' | 'prioridade' | string;
    status_anterior: MarketingStatus | null;
    status_anterior_label: string | null;
    status_novo: MarketingStatus;
    status_novo_label: string;
    motivo: string | null;
    campos: string[];
    campos_labels: string[];
    user: MarketingUsuarioResumo | null;
    created_at: string;
};

export type MarketingLink = {
    id: number;
    marketing_solicitacao_id: number;
    nome: string;
    url: string;
    created_at: string;
};

export type MarketingComentario = {
    id: number;
    marketing_solicitacao_id: number;
    user_id: number;
    conteudo: string;
    tipo: string;
    created_at: string;
    user?: { id: number; name: string };
    mencoes?: { id: number; name: string }[];
};

export type MarketingForm = {
    tipo_material: TipoMaterialValue | '';
    tipo_material_outro?: string;
    sera_impresso?: string;
    dimensao_unidade?: string;
    dimensao_largura?: string;
    dimensao_altura?: string;
    formato?: string;
    formato_outro?: string;
    descricao: string;
    marcas: string[];
    marca_empreendimento?: string;
    informacoes?: string;
    publico: string[];
    publico_outro?: string;
    data_uso: string;
    motivo_prazo_curto?: string;
    links: { nome: string; url: string }[];
    comentario_inicial?: string;
};

export type MarketingListParams = {
    page?: number;
    per_page?: number;
    search?: string;
    status?: string;
    tipo_material?: string;
    setor_id?: number;
    atendente_id?: number;
    sem_responsavel?: '1';
    incluir_concluidas?: '1';
    /** Só as que já passaram por alguma decisão de aprovador (aprovadas, devolvidas ou recusadas). */
    decididas?: '1';
    escopo?: 'minhas' | 'meu_atendimento';
    data_de?: string;
    data_ate?: string;
    sort_by?: string;
    sort_dir?: 'asc' | 'desc';
};

export type Paginated<T> = {
    data: T[];
    meta: { current_page: number; last_page: number; total: number; per_page: number };
};

export type MarketingDashboard = {
    total: number;
    por_status: Record<MarketingStatus, number>;
    sem_responsavel: number;
};

export type TipoMaterialOption = { value: TipoMaterialValue; label: string; dias_uteis: number | null };

// ─── Helpers para remodelar respostas da API ──────────────────────────────────

type PaginationMeta = Paginated<never>['meta'];

type TimelineResponseData<T> = {
    data?: T[];
    meta?: Partial<PaginationMeta>;
    current_page?: number;
    last_page?: number;
    total?: number;
    per_page?: number;
};

// Laravel resource pagination — meta no topo, com fallback pro formato cru do paginator
function reshapeTimeline<T>(r: { data: TimelineResponseData<T> }): Paginated<T> {
    const d = r.data;
    return {
        data: d.data ?? [],
        meta: {
            current_page: d.meta?.current_page ?? d.current_page ?? 1,
            last_page: d.meta?.last_page ?? d.last_page ?? 1,
            total: d.meta?.total ?? d.total ?? 0,
            per_page: d.meta?.per_page ?? d.per_page ?? 20,
        },
    };
}

// ─── API ──────────────────────────────────────────────────────────────────────

export type MarketingMentionable = { id: number; name: string; email: string };

export const marketingApi = {
    list: (p?: MarketingListParams) => api.get('/api/marketing/solicitacoes', { params: p }).then((r) => reshapeTimeline<MarketingSolicitacao>(r)),
    show: (id: number | string) => api.get(`/api/marketing/solicitacoes/${id}`).then((r) => (r.data.data ?? r.data) as MarketingSolicitacao),
    create: (data: MarketingForm) => api.post('/api/marketing/solicitacoes', data).then((r) => (r.data.data ?? r.data) as MarketingSolicitacao),
    update: (id: number | string, data: Partial<MarketingForm>) => api.put(`/api/marketing/solicitacoes/${id}`, data).then((r) => (r.data.data ?? r.data) as MarketingSolicitacao),
    delete: (id: number | string) => api.delete(`/api/marketing/solicitacoes/${id}`),
    // Fluxo de aprovação e atendimento
    aprovar: (id: number | string, d: { observacao?: string; prioridade?: MarketingPrioridade } = {}) =>
        api.post(`/api/marketing/solicitacoes/${id}/aprovar`, d).then((r) => (r.data.data ?? r.data) as MarketingSolicitacao),
    definirPrioridade: (id: number | string, prioridade: MarketingPrioridade) =>
        api.patch(`/api/marketing/solicitacoes/${id}/prioridade`, { prioridade }).then((r) => (r.data.data ?? r.data) as MarketingSolicitacao),
    devolver: (id: number | string, motivo: string, campos: string[]) =>
        api.post(`/api/marketing/solicitacoes/${id}/devolver`, { motivo, campos }).then((r) => (r.data.data ?? r.data) as MarketingSolicitacao),
    recusar: (id: number | string, motivo: string) => api.post(`/api/marketing/solicitacoes/${id}/recusar`, { motivo }).then((r) => (r.data.data ?? r.data) as MarketingSolicitacao),
    reenviar: (id: number | string, data: Partial<MarketingForm> & { comentario?: string }) =>
        api.post(`/api/marketing/solicitacoes/${id}/reenviar`, data).then((r) => (r.data.data ?? r.data) as MarketingSolicitacao),
    assumirAtendimento: (id: number | string) => api.post(`/api/marketing/solicitacoes/${id}/assumir-atendimento`).then((r) => (r.data.data ?? r.data) as MarketingSolicitacao),
    concluir: (id: number | string, observacao?: string) => api.post(`/api/marketing/solicitacoes/${id}/concluir`, { observacao }).then((r) => (r.data.data ?? r.data) as MarketingSolicitacao),
    historico: (id: number | string) => api.get(`/api/marketing/solicitacoes/${id}/historico`).then((r) => (r.data.data ?? r.data) as MarketingMovimentacao[]),
    dashboard: () => api.get('/api/marketing/solicitacoes/dashboard').then((r) => r.data as MarketingDashboard),
    // retorna { data: [{ value, label, dias_uteis }] }
    tiposMaterial: () => api.get('/api/marketing/solicitacoes/tipos-material').then((r) => (r.data.data ?? r.data) as TipoMaterialOption[]),
    exportExcel: (ids: number[]) => api.post('/api/marketing/solicitacoes/export', { ids }, { responseType: 'blob' }),
    // Links — retorna { data: [...] }
    links: (id: number | string) => api.get(`/api/marketing/solicitacoes/${id}/links`).then((r) => (r.data.data ?? r.data) as MarketingLink[]),
    addLink: (id: number | string, d: { nome: string; url: string }) => api.post(`/api/marketing/solicitacoes/${id}/links`, d).then((r) => (r.data.data ?? r.data) as MarketingLink),
    removeLink: (id: number | string, linkId: number) => api.delete(`/api/marketing/solicitacoes/${id}/links/${linkId}`),
    // Timeline — Laravel resource pagination com meta
    timeline: (id: number | string, page = 1) => api.get(`/api/marketing/solicitacoes/${id}/timeline`, { params: { page, per_page: 20 } }).then((r) => reshapeTimeline<MarketingComentario>(r)),
    addComment: (id: number | string, conteudo: string) => api.post(`/api/marketing/solicitacoes/${id}/comentarios`, { conteudo }).then((r) => (r.data.data ?? r.data) as MarketingComentario),
    mentionables: (id: number | string) => api.get(`/api/marketing/solicitacoes/${id}/mentionables`).then((r) => (r.data.data ?? r.data) as MarketingMentionable[]),
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function formatDate(iso: string | null | undefined): string {
    if (!iso) return '—';
    const [y, m, d] = iso.slice(0, 10).split('-');
    return `${d}/${m}/${y}`;
}

/** O SLA só começa na aprovação do 2º nível — antes disso não há data_prazo. */
export function slaAguardandoAprovacao(status: MarketingStatus): boolean {
    return status === 'em_aprovacao' || status === 'em_aprovacao_2' || status === 'devolvida_ajustes';
}

export function slaInfo(dataPrazo: string | null, status: MarketingStatus): { label: string; cls: string } {
    if (!dataPrazo && slaAguardandoAprovacao(status)) {
        return { label: 'Inicia após aprovação', cls: 'text-gray-400' };
    }
    if (status === 'concluida' || status === 'recusada' || !dataPrazo) {
        return { label: '—', cls: 'text-gray-400' };
    }
    const diff = Math.ceil((new Date(dataPrazo).getTime() - Date.now()) / 86_400_000);
    if (diff < 0) return { label: `Vencido ${Math.abs(diff)}d`, cls: 'text-danger font-medium' };
    if (diff === 0) return { label: 'Vence hoje', cls: 'text-warning font-medium' };
    return { label: `${diff}d`, cls: diff <= 3 ? 'text-warning' : 'text-gray-500 dark:text-white-dark' };
}

export const SEM_RESPONSAVEL = 'Não atribuído';

/**
 * Mensagem amigável de erro. 409 = alguém movimentou a solicitação antes;
 * a mensagem do back-end já explica o que aconteceu.
 */
export function mensagemDoErro(error: unknown, fallback: string): string {
    const e = error as { response?: { status?: number; data?: { message?: string } } };
    const msg = e?.response?.data?.message;
    if (typeof msg === 'string' && msg.trim() !== '') return msg;
    return fallback;
}

export function ehConflito(error: unknown): boolean {
    return (error as { response?: { status?: number } })?.response?.status === 409;
}

export function initials(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
